import React, { useState, useRef, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FaLockOpen, FaLock, FaArrowUp, FaEdit } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Server from "../../Shared/Server";
import { months_list } from '../../Shared/List';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const emptyCellFormatter = (params) =>
  params.value === null || params.value === undefined || params.value === '' ? '-' : params.value;

const ServerSideUnBilled = () => {
  const [show, setShow] = useState(false);
  const [lockData, setLockData] = useState('');
  const [isLocking, setIsLocking] = useState(false);
  const gridApi = useRef();
  const gridRef = useRef();
  const navigate = useNavigate();
  const location = useLocation();

  const { payload } = location.state;
  const title = `${months_list[payload.month - 1].name} - ${payload.year} All Un-Billed Customers`;

  const columnDefs = useMemo(() => [
    {
      headerName: "#",
      filter: false,
      sortable: false,
      maxWidth: 70,
      valueFormatter: (params) => (params.node.rowIndex + 1).toString(),
    },
    {
      headerName: "Actions",
      minWidth: 200,
      pinned: 'left',
      sortable: false,
      filter: false,
      cellRenderer: (e) => {
        const row = e.data;
        if (!row) return null;

        const lockPayload = {
          year: row.year,
          month: row.month,
          public_id: row.public_id,
          previous_reading: row.current_reading,
          by: 'serverside',
        };

        const billUpData = {
          year: row.year,
          month: row.month,
          public_id: row.public_id,
          previous_reading: row.previous_reading,
          remark: row.remark,
          serial_number: row.serial_number,
          by: 'serverside',
          location_id: row.location_pk,
          zone_id: row.zone_id,
        };

        const editData = {
          public_id: row.public_id,
          serial_number: row.serial_number,
          lock_number: row.lock_number,
          contact_number: row.contact_number,
        };

        return (
          <div className="d-flex gap-1">
            {row.caution_status === 'lock' ? (
              <button
                className="btn btn-outline-success"
                onClick={() => handleShow(lockPayload)}
                title="Unlock"
              >
                <FaLockOpen />
              </button>
            ) : (
              <span className="btn btn-outline-danger disabled" title="Locked">
                <FaLock />
              </span>
            )}
            <Link
              className="btn btn-outline-info"
              to="/add-bill"
              state={{ data: billUpData }}
              title="Bill Up"
            >
              <FaArrowUp />
            </Link>
            <Link
              className="btn btn-outline-warning"
              to="/un-billed-update"
              state={{ data: editData }}
              title="Edit"
            >
              <FaEdit />
            </Link>
          </div>
        );
      },
    },
    { headerName: "PID", field: "previous_id", minWidth: 110 },
    { headerName: "ID", field: "location_id", minWidth: 110 },
    { headerName: "Name", field: "full_name", minWidth: 220 },
    { headerName: "Remark", field: "remark" },
    { headerName: "Contact", field: "contact_number" },
    { headerName: "Status", field: "status" },
    { headerName: "Meter ID", field: "serial_number" },
    { headerName: "Lock ID", field: "lock_number" },
    { headerName: "Previous Reading", field: "previous_reading" },
    { headerName: "Rate", field: "rate_name" },
    { headerName: "Village", field: "village_name" },
    { headerName: "Zone", field: "zone_name" },
    { headerName: "Station", field: "station_name" },
    { headerName: "Line", field: "line_name" },
  ], []);

  const defaultColDef = useMemo(() => ({
    sortable: false,
    filter: true,
    resizable: true,
    valueFormatter: emptyCellFormatter,
    filterParams: {
      buttons: []
    }
  }), []);

  const fetchData = async (params) => {
    const dataColumns = columnDefs.filter(col => col.field);
    const requestParams = {
      draw: 1,
      start: params.startRow,
      length: params.endRow - params.startRow,
      year: parseInt(payload.year),
      month: parseInt(payload.month),
      columns: dataColumns.map(col => ({
        data: col.field,
        searchable: col.filter !== false,
        orderable: !!col.sortable,
        search: { value: '', regex: false }
      })),
      order: params.sortModel.map(sort => ({
        column: dataColumns.findIndex(col => col.field === sort.colId),
        dir: sort.sort
      })),
      search: Object.keys(params.filterModel).map(key => ({
        col: key,
        value: params.filterModel[key].filter?.toString().toLowerCase() ?? '',
        type: params.filterModel[key].type || "contains"
      })),
      filter: {}
    };

    try {
      const response = await Server.post('/un-billed/serverside', requestParams);
      params.successCallback(response.data.data, response.data.recordsFiltered);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to load un-billed');
      params.failCallback();
    }
  };

  const onGridReady = (params) => {
    gridApi.current = params.api;
    params.api.updateGridOptions({
      datasource: {
        getRows: (p) => fetchData(p)
      }
    });
  };

  const onFirstDataRendered = (params) => {
    params.api.autoSizeAllColumns();
  };

  const onGridSizeChanged = (params) => {
    params.api.autoSizeAllColumns();
  };

  const handleClose = () => {
    if (isLocking) return;
    setShow(false);
  };

  const handleShow = (data) => {
    setShow(true);
    setLockData(data);
  };

  const handleLock = async (data) => {
    setIsLocking(true);
    try {
      const response = await Server.post(`/un-billed/${data.public_id}/lock`);
      if (response.status === 201) {
        const navPayload = {
          year: data.year,
          month: data.month,
          public_id: data.public_id2,
          by: data.by,
          name: data.name,
        };
        navigate(`/un-billed-${data.by}`, { state: { data: navPayload } });
        gridApi.current?.purgeInfiniteCache();
      }
      setShow(false);
    } catch (error) {
      if (error?.response?.status === 401) {
        navigate('/login');
      } else if (error?.response?.status === 403) {
        toast.error(error.response.data.detail);
        navigate('/');
      } else {
        toast.error(error?.response?.data?.detail || 'Failed to unlock');
      }
    } finally {
      setIsLocking(false);
    }
  };

  return (
    <>
      <div className="card">
        <div className="card-header p-2 bg-info text-white d-flex justify-content-center">
          <h3 className="card-title mb-0">{title}</h3>
        </div>
        <div className="card-body">
          <div className="ag-theme-alpine responsive-aggrid" style={{ height: 400, width: '100%' }}>
            <AgGridReact
              ref={gridRef}
              rowModelType="infinite"
              cacheBlockSize={25}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              rowSelection="multiple"
              enableCellTextSelection={true}
              suppressCellFocus={true}
              onGridReady={onGridReady}
              onFirstDataRendered={onFirstDataRendered}
              onGridSizeChanged={onGridSizeChanged}
            />
          </div>
        </div>
      </div>

      <Modal show={show} onHide={handleClose} backdrop={isLocking ? 'static' : true}>
        <Modal.Header closeButton={!isLocking}>
          <Modal.Title>Unlock Customer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to unlock this customer?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={isLocking}>
            No
          </Button>
          <Button variant="danger" onClick={() => handleLock(lockData)} disabled={isLocking}>
            {isLocking ? 'Unlocking…' : 'Yes, unlock'}
          </Button>
        </Modal.Footer>
      </Modal>
      <br />
    </>
  );
};

export default ServerSideUnBilled;
